/**
 * Shift Copying Database Operations
 * 
 * Database-specific functions extracted from useShiftCopying hook.
 * Handles all Supabase operations for shift copying.
 */

import { supabase as supabaseClient } from '@/integrations/supabase/client';
// import { ShiftToInsert, createDayTimeRange, createMonthQueryRange } from './shift-copying-utils';

// Temporary types and functions until shift-copying-utils is implemented
export interface ShiftToInsert {
  business_id: string;
  start_time: string;
  end_time: string;
  title?: string;
  employee_id?: string | null;
}

const createDayTimeRange = (targetDay: Date) => {
  const start = new Date(targetDay);
  start.setHours(0, 0, 0, 0);
  const end = new Date(targetDay);
  end.setHours(23, 59, 59, 999);
  return {
    start: start.toISOString(),
    end: end.toISOString()
  };
};

const createMonthQueryRange = (targetMonth: Date) => {
  const start = new Date(targetMonth.getFullYear(), targetMonth.getMonth(), 1);
  const end = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return {
    start: start.toISOString(),
    end: end.toISOString()
  };
};

export interface DatabaseError {
  message: string;
  details?: any;
  hint?: string;
  code?: string;
}

/**
 * Deletes all existing shifts for a specific day
 */
export const deleteShiftsForDay = async (
  targetDay: Date,
  businessId: string
): Promise<{ success: boolean; error?: DatabaseError }> => {
  try {
    const timeRange = createDayTimeRange(targetDay);
    
    const { error } = await supabaseClient
      .from('shifts')
      .delete()
      .eq('business_id', businessId)
      .gte('start_time', timeRange.start)
      .lte('end_time', timeRange.end);

    if (error) {
      return {
        success: false,
        error: {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        }
      };
    }

    return { success: true };
  } catch (err: any) {
    return {
      success: false,
      error: {
        message: err.message || 'Unknown error occurred',
        details: err,
      }
    };
  }
};

/**
 * Inserts new shifts for a specific day
 */
export const insertShiftsForDay = async (
  shiftsToInsert: ShiftToInsert[]
): Promise<{ success: boolean; error?: DatabaseError }> => {
  try {
    const { error } = await supabaseClient
      .from('shifts')
      .insert(shiftsToInsert);

    if (error) {
      return {
        success: false,
        error: {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        }
      };
    }

    return { success: true };
  } catch (err: any) {
    return {
      success: false,
      error: {
        message: err.message || 'Unknown error occurred',
        details: err,
      }
    };
  }
};

/**
 * Copies shifts to a single target day (delete existing + insert new)
 */
export const copyShiftsToSingleDay = async (
  targetDay: Date,
  shiftsToInsert: ShiftToInsert[]
): Promise<{ success: boolean; error?: DatabaseError }> => {
  // Delete existing shifts first
  const deleteResult = await deleteShiftsForDay(targetDay, shiftsToInsert[0]?.business_id);
  if (!deleteResult.success) {
    return deleteResult;
  }

  // Insert new shifts
  return await insertShiftsForDay(shiftsToInsert);
};

/**
 * Validates that shifts were successfully copied by checking the database
 */
export const validateCopiedShifts = async (
  businessId: string,
  targetMonth: Date
): Promise<{ startTimes: string[]; error?: DatabaseError }> => {
  try {
    const queryRange = createMonthQueryRange(targetMonth);
    
    const { data: copiedShifts, error } = await supabaseClient
      .from('shifts')
      .select('start_time')
      .eq('business_id', businessId)
      .gte('start_time', queryRange.start)
      .lt('start_time', queryRange.end);

    if (error) {
      return {
        startTimes: [],
        error: {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        }
      };
    }

    return {
      startTimes: copiedShifts?.map(shift => shift.start_time) || [],
    };
  } catch (err: any) {
    return {
      startTimes: [],
      error: {
        message: err.message || 'Unknown error occurred',
        details: err,
      }
    };
  }
};

/**
 * Batch operation to copy shifts to multiple days with error handling
 */
export const batchCopyShifts = async (
  targetDates: Date[],
  shiftsTemplate: ShiftToInsert[],
  onProgress?: (completed: number, total: number, currentDate: Date) => void
): Promise<{
  successCount: number;
  errorCount: number;
  errors: Array<{ date: Date; error: DatabaseError }>;
}> => {
  let successCount = 0;
  let errorCount = 0;
  const errors: Array<{ date: Date; error: DatabaseError }> = [];

  for (let i = 0; i < targetDates.length; i++) {
    const targetDate = targetDates[i];
    
    // Create shifts specific to this target date
    const shiftsForThisDate = shiftsTemplate.map(template => ({
      ...template,
      start_time: template.start_time.replace(
        template.start_time.substring(0, 10),
        targetDate.toISOString().substring(0, 10)
      ),
      end_time: template.end_time.replace(
        template.end_time.substring(0, 10),
        targetDate.toISOString().substring(0, 10)
      ),
    }));

    const result = await copyShiftsToSingleDay(targetDate, shiftsForThisDate);
    
    if (result.success) {
      successCount++;
    } else {
      errorCount++;
      if (result.error) {
        errors.push({ date: targetDate, error: result.error });
      }
    }

    // Report progress if callback provided
    if (onProgress) {
      onProgress(i + 1, targetDates.length, targetDate);
    }
  }

  return { successCount, errorCount, errors };
};